import React, { useEffect, useState, useCallback } from 'react';
import { Header } from '../../components/layout/Header';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    BarChart3,
    Save,
    RefreshCw,
    Target,
    TrendingUp
} from 'lucide-react';

// Standalone — must be outside parent to keep input focus
const MetricInput = React.memo(({ label, value, onChange, accent }) => (
    <div className="space-y-1">
        <Label className={`text-xs ${accent === 'red' ? 'text-brand-red font-bold' : accent === 'green' ? 'text-status-success font-bold' : 'text-content-secondary'}`}>{label}</Label>
        <Input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`input-base h-9 ${accent === 'red' ? 'border-brand-red/30' : accent === 'green' ? 'border-status-success/30' : ''}`}
            placeholder="0"
        />
    </div>
));

const defaultMetrics = {
    target_accounts: '',
    total_contacts: '',
    total_reachouts: '',
    connected: '',
    positive_responses: '',
    rejects: '',
    future_leads: '',
    mqls: '',
    qualified_leads: '',
    funnel_reach: '',
    funnel_connect: '',
    funnel_nurture: '',
    funnel_response: '',
    funnel_future_leads: '',
    funnel_positive_response: '',
    funnel_mql: '',
    funnel_sql: '',
};

export default function AdminOverallMetricsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [metrics, setMetrics] = useState({ ...defaultMetrics });

    useEffect(() => {
        loadCustomers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMetrics = useCallback(async (customerId) => {
        if (!customerId) return;
        try {
            const response = await adminApi.getOverallMetrics(customerId);
            if (response.data) {
                const d = response.data;
                setMetrics({
                    target_accounts: d.target_accounts ?? '',
                    total_contacts: d.total_contacts ?? '',
                    total_reachouts: d.total_reachouts ?? '',
                    connected: d.connected ?? '',
                    positive_responses: d.positive_responses ?? '',
                    rejects: d.rejects ?? '',
                    future_leads: d.future_leads ?? '',
                    mqls: d.mqls ?? '',
                    qualified_leads: d.qualified_leads ?? '',
                    funnel_reach: d.funnel_reach ?? '',
                    funnel_connect: d.funnel_connect ?? '',
                    funnel_nurture: d.funnel_nurture ?? '',
                    funnel_response: d.funnel_response ?? '',
                    funnel_future_leads: d.funnel_future_leads ?? '',
                    funnel_positive_response: d.funnel_positive_response ?? '',
                    funnel_mql: d.funnel_mql ?? '',
                    funnel_sql: d.funnel_sql ?? '',
                });
            } else {
                setMetrics({ ...defaultMetrics });
            }
        } catch {
            setMetrics({ ...defaultMetrics });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCustomers = async () => {
        try {
            const response = await adminApi.getCustomers();
            setCustomers(response.data);
            if (response.data.length > 0 && response.data[0].details?.id) {
                const firstId = response.data[0].details.id;
                setSelectedCustomer(firstId);
                await loadMetrics(firstId);
            }
        } catch {
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = async (customerId) => {
        setSelectedCustomer(customerId);
        await loadMetrics(customerId);
    };

    const handleSave = async () => {
        if (!selectedCustomer) {
            toast.error('Please select a customer');
            return;
        }
        setSaving(true);
        try {
            const payload = { customer_id: selectedCustomer };
            Object.keys(defaultMetrics).forEach(key => {
                payload[key] = parseInt(metrics[key]) || 0;
            });
            await adminApi.saveOverallMetrics(payload);
            toast.success('Overall metrics saved successfully');
        } catch {
            toast.error('Failed to save metrics');
        } finally {
            setSaving(false);
        }
    };

    const updateMetric = (field, value) => {
        setMetrics(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="admin-overall-metrics-page">
            <Header 
                title="Overall Metrics"
                subtitle="Set overall dashboard metrics for each customer"
            />

            <div className="p-8 space-y-6">
                {/* Customer Selection */}
                <div className="card-base p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-brand-red/10">
                                <BarChart3 className="w-6 h-6 text-brand-red" />
                            </div>
                            <div>
                                <h3 className="text-lg font-heading font-bold text-white">Select Customer</h3>
                                <p className="text-sm text-content-muted">Choose a customer to update their dashboard metrics</p>
                            </div>
                        </div>
                        <Select value={selectedCustomer} onValueChange={handleCustomerChange}>
                            <SelectTrigger className="input-base w-64" data-testid="customer-selector">
                                <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface-card border-line-default">
                                {customers.filter(c => c.details?.id).map((c) => (
                                    <SelectItem key={c.details.id} value={c.details.id}>
                                        {c.details?.company_name || c.user.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {selectedCustomer && (
                    <>
                        {/* Key Metrics Section */}
                        <div className="card-base p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Target className="w-5 h-5 text-brand-red" />
                                <h3 className="text-lg font-heading font-bold text-white">KEY METRICS</h3>
                                <span className="text-xs text-content-muted">
                                    (These appear on the customer's dashboard)
                                </span>
                            </div>
                            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
                                <MetricInput label="Target Accounts" value={metrics.target_accounts} onChange={(v) => updateMetric('target_accounts', v)} />
                                <MetricInput label="Total Contacts" value={metrics.total_contacts} onChange={(v) => updateMetric('total_contacts', v)} />
                                <MetricInput label="Total Reachouts" value={metrics.total_reachouts} onChange={(v) => updateMetric('total_reachouts', v)} />
                                <MetricInput label="Connected" value={metrics.connected} onChange={(v) => updateMetric('connected', v)} />
                                <MetricInput label="Positive Responses" value={metrics.positive_responses} onChange={(v) => updateMetric('positive_responses', v)} />
                                <MetricInput label="Future Leads" value={metrics.future_leads} onChange={(v) => updateMetric('future_leads', v)} />
                                <MetricInput label="MQLs" value={metrics.mqls} onChange={(v) => updateMetric('mqls', v)} accent="red" />
                                <MetricInput label="Qualified Leads" value={metrics.qualified_leads} onChange={(v) => updateMetric('qualified_leads', v)} accent="green" />
                            </div>
                        </div>

                        {/* Funnel Data Section */}
                        <div className="card-base p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <TrendingUp className="w-5 h-5 text-channel-linkedin" />
                                <h3 className="text-lg font-heading font-bold text-white">FUNNEL DATA</h3>
                                <span className="text-xs text-content-muted">
                                    (These appear in the funnel visualization)
                                </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                                <MetricInput label="Reach" value={metrics.funnel_reach} onChange={(v) => updateMetric('funnel_reach', v)} />
                                <MetricInput label="Connect" value={metrics.funnel_connect} onChange={(v) => updateMetric('funnel_connect', v)} />
                                <MetricInput label="Nurture" value={metrics.funnel_nurture} onChange={(v) => updateMetric('funnel_nurture', v)} />
                                <MetricInput label="Response" value={metrics.funnel_response} onChange={(v) => updateMetric('funnel_response', v)} />
                                <MetricInput label="Future Leads" value={metrics.funnel_future_leads} onChange={(v) => updateMetric('funnel_future_leads', v)} />
                                <MetricInput label="Positive Response" value={metrics.funnel_positive_response} onChange={(v) => updateMetric('funnel_positive_response', v)} />
                                <MetricInput label="MQL" value={metrics.funnel_mql} onChange={(v) => updateMetric('funnel_mql', v)} accent="red" />
                                <MetricInput label="SQL" value={metrics.funnel_sql} onChange={(v) => updateMetric('funnel_sql', v)} accent="green" />
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end">
                            <Button onClick={handleSave} disabled={saving} className="btn-primary gap-2" data-testid="save-metrics-btn">
                                {saving ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Save Overall Metrics
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
